import { Injectable } from '@angular/core';
import { PlanDetailEntityExtended, PlanPriceEntityExtended, PlanEntityExtended, SubscriptionTransactionEntity, BillingCycleEntity, InstanceEntityExtended, SubscriptionHistoryEntity, SubscriptionEntityExtended, DesiredFeatureLimits, PlanDetailFeatureEntityExtended, AskSkipMetadataEngine } from 'mj_generatedentities';
import { LogError, LogStatus, Metadata, RunView } from '@memberjunction/core';
import { gql, GraphQLDataProvider } from '@memberjunction/graphql-dataprovider';
import { SharedService } from './shared-service';
import { BehaviorSubject, filter } from 'rxjs';
import { DialogService } from '@progress/kendo-angular-dialog';
import { UpgradePromptModalComponent } from 'src/app/shared/upgrade-prompt-modal/upgrade-prompt-modal.component';
import { Router } from '@angular/router';

export interface PlanDetails {
  Plan: PlanEntityExtended;
  Constraints: PlanDetailEntityExtended;
  Prices: PlanPriceEntityExtended[];
  Features: string[];
}

export interface PlanCard {
  ID: string;
  Title: string;
  // Price data is structured as a map with keys as billing cycles IDs (e.g., 'Monthly', 'Annual') to
  // corresponding price details (billing cycle name, price, and price ID).
  PriceData: Map<
    string, 
    {
      BillingCycle: string;
      Price: number;
      PriceID: string;
    }
  >;
  Features: string[];
  Sequence: number;
}

export interface SubscriptionPreviewOptions {
  NewPriceID?: string;
  NewUserCount?: number;
}

export interface SubscriptionUpdateOptions {
  NewPriceID?: string;
  NewInstanceCount?: number;
  CancelSubscription?: boolean;
}

export interface CurrentPeriod {
  start: string; // ISO date string; convert to Date if needed
  end: string;
}

export interface StripeInvoiceLineItem {
  description?: string;
  quantity?: number;
  unitPrice?: number;
  amount?: number;
}

export interface StripePaidInvoice {
  id: string;
  amountDue?: number;
  currency?: string;
  status?: string;
  invoiceNumber?: string;
  customerEmail?: string;
  dueDate?: string; // ISO date string
  created?: string; // ISO date string
  subtotal?: number;
  tax?: number;
  total?: number;
  lines?: StripeInvoiceLineItem[];
}

export interface StripeUpcomingInvoice {
  currency?: string;
  status?: string;
  amountDue?: number;
  subtotal?: number;
  tax?: number;
  total?: number;
  billingDate?: Date; // ISO date string
  periodStart?: Date; // ISO date string
  periodEnd?: Date; // ISO date string
  totalExcludingTax?: number;
  appliedBalance?: number;
  lines?: StripeInvoiceLineItem[];
}

export interface StripePaymentMethodData {
  id: string;
  type: string;
  card?: {
    brand: string;
    last4: string;
    exp_month?: number;
    exp_year?: number;
  };
}

export interface StripeSubscriptionData {
  upcomingInvoice?: StripeUpcomingInvoice;
  invoiceHistory?: StripePaidInvoice[];
  currentPeriod?: CurrentPeriod;
  paymentMethod?: StripePaymentMethodData;
}

export interface SubscriptionUpdatePreview {
  oldPlanName?: string;
  newPlanName?: string;
  lineItems?: StripeInvoiceLineItem[];
  subtotal?: number;
  tax?: number;
  total?: number;
  appliedBalance?: number;
  nextPaymentAttempt?: string; // or Date
  currency?: string;
}

const POLLING_INTERVAL_MS = 500; // Polling interval in milliseconds
const POLLING_TIMEOUT_MS = 15000; // Timeout for polling in milliseconds

@Injectable({
  providedIn: 'root'
})
export class SubscriptionPlanService {
  private plans: PlanDetails[] = []; // Cache of plans
  private billingPeriods: BillingCycleEntity[] = [];
  private currentTransaction: SubscriptionTransactionEntity;

  private _planDataSource = new BehaviorSubject<boolean>(false);
  planDataLoaded$ = this._planDataSource.asObservable();

  constructor(
    private sharedService: SharedService,
    private router: Router,
    private dialogService: DialogService
  ) {
    this.plans = [];
    this.currentTransaction = null;

    sharedService.setupComplete$.pipe(
      filter((setupComplete) => setupComplete)).subscribe(async () => 
        {
          await this.LoadPlans();
        });
  }

  public get CurrentTransaction(): SubscriptionTransactionEntity {
    return this.currentTransaction;
  }

  public async LoadPlans(): Promise<void> {
    if (this.plans.length > 0) {
      return;
    }

    const md = new Metadata();

    let plans = AskSkipMetadataEngine.Instance.Plans;
    let details = AskSkipMetadataEngine.Instance.PlanDetails;
    let prices = AskSkipMetadataEngine.Instance.PlanPrices;
    let features = AskSkipMetadataEngine.Instance.PlanDetailFeatures;
    let billingPeriods = AskSkipMetadataEngine.Instance.BillingCycles;

    if (!plans || !details || !prices || !features || !billingPeriods) {
      console.log('Metadata not available. Loading metadata...');
      AskSkipMetadataEngine.Instance.Config(true, md.CurrentUser);
      plans = AskSkipMetadataEngine.Instance.Plans;
      details = AskSkipMetadataEngine.Instance.PlanDetails;
      prices = AskSkipMetadataEngine.Instance.PlanPrices;
      features = AskSkipMetadataEngine.Instance.PlanDetailFeatures;
      billingPeriods = AskSkipMetadataEngine.Instance.BillingCycles;
    }

    this.plans = this.constructSubscriptionPlan(plans, details, prices, features);
    this.billingPeriods = billingPeriods.sort((a, b) => a.NumMonths - b.NumMonths);

    LogStatus('Subscripton Plan Service loaded plan data successfully.');
    this._planDataSource.next(true);
  }

  public get Plans(): PlanDetails[] {
    return this.plans;
  }

  public get BillingPeriods(): BillingCycleEntity[] {
    return this.billingPeriods;
  }

  public get PlanCards(): PlanCard[] {
    return this.plans.map((p) => {
      const priceData = new Map<string, { BillingCycle: string; Price: number; PriceID: string }>();

      p.Prices.forEach((price) => {
        priceData.set(price.BillingCycleID, 
          {
            BillingCycle: price.BillingCycle,
            Price: price.Price,
            PriceID: price.ID
          });
      });

      return {
        ID: p.Plan.ID,
        Title: p.Plan.Name,
        PriceData: priceData,
        Features: p.Features,
        Sequence: p.Plan.Sequence
      };
    });
  }

  public async InitializeTransaction(
    organizationContactID: string,
    priceID: string
  ): Promise<void> {
    const md = new Metadata();
    const transaction = await md.GetEntityObject<SubscriptionTransactionEntity>('Subscription Transactions');
    transaction.NewRecord();

    transaction.OrganizationContactID = organizationContactID;
    transaction.PlanPriceID = priceID;
    transaction.Status = 'Initialized';

    const saveResult = await transaction.Save();

    if (saveResult) {
      this.currentTransaction = transaction;
    } else {
      throw new Error(`Failed to create Stripe transaction: ${transaction.LatestResult}`);
    }
  }


  /**
   * Retrieves the Stripe client secret for a given subscription transaction.
   * This function calls the getStripeTransactionClientSecret resolver.
   * @param ID The ID of the subscription transaction.
   * @returns A promise that resolves to the client secret.
   */
  public async GetStripeTransactionClientSecret(): Promise<string> {
    if (!this.currentTransaction || !this.currentTransaction.ID) {
      throw new Error('Invalid transaction.');
    }

    const ID = this.currentTransaction.ID;

    const query = gql`
      query GetStripeTransactionClientSecret($ID: String!) {
        GetStripeTransactionClientSecret(ID: $ID)
      }
    `;
    const result = await GraphQLDataProvider.Instance.ExecuteGQL(query, { ID });
    return result.GetStripeTransactionClientSecret;
  }

  /**
   * Retrieves the billing cycle for a given subscription.
   * @param subscription The subscription entity.
   * @returns The billing cycle as 'Monthly' or 'Annual'.
   */
  public GetBillingCycle(subscription: SubscriptionEntityExtended): BillingCycleEntity{
    if (!subscription || !subscription.PlanPriceID) {
      return null;
    }

    const planPrice = this.plans
      .flatMap((p) => p.Prices)
      .find((pp) => pp.ID === subscription.PlanPriceID);

    if (!planPrice) {
      return null;
    }

    const billingCycle = this.billingPeriods.find((bp) => bp.ID === planPrice.BillingCycleID);
    if (!billingCycle) {
      return null;
    }

    return billingCycle;
  }

  /**
    * Calculates the recurring invoice amount for a given subscription.
    * @param subscription The subscription entity.
    * @returns An object containing the amount and billing cycle in the number of months.
    */
  public CalculateRecuringInvoiceAmount(subscription: SubscriptionEntityExtended): { amount: number, billingPeriodInMonths: number } {
    if (!subscription || !subscription.PlanPriceID) {
      return { amount: 0, billingPeriodInMonths: 1 };
    }

    const planPrice = this.plans
      .flatMap((p) => p.Prices)
      .find((pp) => pp.ID === subscription.PlanPriceID);

    const billingPeriod = this.billingPeriods.find((bp) => bp.ID === planPrice.BillingCycleID);

    if (!planPrice) {
      return { amount: 0, billingPeriodInMonths: 1 };
    }

    // Note that all plans are per user per billing cycle
    const quantity = subscription.Quantity || 1;
    const amount = planPrice.Price * quantity;
    const billingPeriodInMonths = billingPeriod ? billingPeriod.NumMonths : 1;
    return { amount, billingPeriodInMonths };
  }

  public async GetInstances(subscription: SubscriptionEntityExtended): Promise<InstanceEntityExtended[]> {
    if (!subscription || !subscription.ID) {
      return [];
    }

    const rv = new RunView();
    const results = await rv.RunView<InstanceEntityExtended>({
      EntityName: 'Instances',
      ExtraFilter: `SubscriptionID = '${subscription.ID}'`,
      OrderBy: 'Name ASC',
      ResultType: 'entity_object'
    });

    if (results.Success && results.Results.length > 0) {
      return results.Results;
    }
    return [];
  }

  public async UpdateSubscription(subscription: SubscriptionEntityExtended, updatedByContactID: string, options: SubscriptionUpdateOptions): Promise<{ Success: boolean, Message: string }> {
    if (!subscription || !subscription.ID) {
      return { Success: false, Message: 'Invalid subscription.' };
    }

    const newPriceID = options.NewPriceID;
    const newInstanceCount = options.NewInstanceCount;
    const cancelSubscription = options.CancelSubscription;

    // We can only update either the price, user count, or instance count at a time
    if (!newPriceID && !newInstanceCount && !cancelSubscription) {
      return { Success: false, Message: 'No changes provided.' };
    }

    // Make sure that only one of the three parameters (newPriceID, newUserCount, newInstanceCount) is provided
    const changesCount = [newPriceID, newInstanceCount, cancelSubscription].filter(value => value !== undefined).length;
    if (changesCount > 1) {
      return { Success: false, Message: 'Only one change can be made at a time.' };
    }

    // Instead of directly updating the subscription, we will create a subscription history record and set the status change to 'Pending'
    // On the backend, the subscription history record will queue the changes to be applied to the subscription using the Stripe API
    // When the changes take affect, the subscription will be updated and the history record will be marked as 'Complete' or 'Failed'
    // We will need to continuously need to check the status of the subscription history record until it is marked as 'Complete' or 'Failed'
    const md = new Metadata();
    const historyRecord = await md.GetEntityObject<SubscriptionHistoryEntity>('Subscription Histories');

    historyRecord.NewRecord();
    historyRecord.SubscriptionID = subscription.ID;
    historyRecord.UpdatedByContactID = updatedByContactID;
    historyRecord.ChangeDate = new Date();

    if (newPriceID) {
      this.handlePlanPriceChange(historyRecord, subscription.PlanPriceID, newPriceID);
    }

    // User count updates are now handled automatically by the server-side InstanceContactEntityServer.Save() method
    // Commenting out this section to prevent double counting
    /*
    if (newUserCount) {
      this.handleUserCountChange(historyRecord, subscription.Quantity, newUserCount);
    }
    */

    if (newInstanceCount) {
      this.handleInstanceCountChange(historyRecord, subscription.InstanceCount, newInstanceCount);
    }

    if (cancelSubscription) {
      historyRecord.ChangeType = 'Canceling';
    }

    // Check to see if the subscription mutation is valid
    const result = await subscription.ValidateUpdate(historyRecord);
    if (!result.IsValid) {
      return { Success: false, Message: `Validation failed: ${result.Message}` };
    }

    const saveResult = await historyRecord.Save();

    if (saveResult) {
      const historyID = historyRecord.ID;
      // const pollingHistoryRecord = await md.GetEntityObject<SubscriptionHistoryEntity>('Subscription Histories');

      // Polling logic to check the status of the subscription history record with a 15-second timeout
      let isComplete = false;
      const startTime = Date.now();
      while (!isComplete) {
        if (Date.now() - startTime > POLLING_TIMEOUT_MS) {
          return { Success: false, Message: 'Timeout: polling ended without a complete status.' };
        }
        await new Promise(resolve => setTimeout(resolve, POLLING_INTERVAL_MS));
        await historyRecord.Load(historyID); // Reload the history record to get the latest status
        const status = historyRecord.ChangeStatus;
        isComplete = status === 'Complete' || historyRecord.ChangeStatus === 'Failed';
      }

      if (historyRecord.ChangeStatus === 'Complete') {
        // Refresh the subscription data after the changes have been applied
        const subscriptionID = subscription.ID;
        if (await subscription.Load(subscriptionID)) { // Reload the subscription to get the latest data
          return { Success: true, Message: 'Subscription updated successfully.' };
        } else {
          const message = subscription.LatestResult.Message || subscription.LatestResult.Errors?.map(e => e.Message).join(', ') || 'Failed to load updated subscription data.';
          return { Success: false, Message: message };
        }
      } else if (historyRecord.ChangeStatus === 'Failed') {
        const message = historyRecord.LatestResult.Message || historyRecord.LatestResult.Errors?.map(e => e.Message).join(', ') || 'Failed to apply subscription changes.';
        return { Success: false, Message: message };
      }
    } else {
      const message = historyRecord.LatestResult.Message || historyRecord.LatestResult.Errors?.map(e => e.Message).join(', ') || 'Failed to save subscription history record.';
      LogError(`Failed to save subscription history record: ${message}`);
      return { Success: false, Message: message };
    }

    return { Success: false, Message: 'An unknown error occurred.' };
  }

  async OpenUpgradeModal(subscription: SubscriptionEntityExtended, featureLimits: DesiredFeatureLimits): Promise<void> {
    // Set the subscription data to the dialog
    const currentPrice = await subscription.GetPlanPrice();
    const billingCycle = await subscription.GetBillingCycle();
    const upgradePrice = await subscription.GetNextPlanTier(featureLimits);
    const upgradePlan = this.plans.find(p => p.Plan.ID === upgradePrice?.PlanID)?.Plan;
    let upgradeFeatures = [];
    let upgradeCostMontly = 0;
    let triggerMessage = '';

    if (upgradePrice && upgradePlan && currentPrice.Price < upgradePrice.Price) {
      upgradeFeatures = this.plans.find(p => p.Plan.ID === upgradePrice.PlanID).Features;
      upgradeCostMontly = ((upgradePrice.Price - currentPrice.Price) * subscription.Quantity)/billingCycle.NumMonths;

      if (featureLimits.MaxUser) {
        triggerMessage = `You are at the maximum user limit for your current plan. Please upgrade to ${upgradePlan.Name} to add more users.`;
      } else if (featureLimits.MaxInstance) {
        triggerMessage = `You are at the maximum instance limit for your current plan. Please upgrade to ${upgradePlan.Name} to add more instances.`;
      } else if (featureLimits.MaxConnector) {
        triggerMessage = `You are at the maximum connector limit for your current plan. Please upgrade to ${upgradePlan.Name} to add more connectors.`;
      } else if (featureLimits.MaxSavedReport) {
        triggerMessage = `You are at the maximum saved report limit for your current plan. Please upgrade to ${upgradePlan.Name} to save more reports.`;
      } else if (featureLimits.ReportSharingEnabled) {
        triggerMessage = `Report sharing is disabled in your current plan. Please upgrade to ${upgradePlan.Name} to enable report sharing.`;
      } else if (featureLimits.ReportSchedulingEnabled) {
        triggerMessage = `Report scheduling is disabled in your current plan. Please upgrade to ${upgradePlan.Name} to enable report scheduling.`;
      }
    }

    const dialogRef = this.dialogService.open({
      content: UpgradePromptModalComponent,
      width: 450,
      height: 400,
      title: 'Upgrade Your Plan'
    });
  
    dialogRef.content.instance.noUpgradeAvailable = !upgradePrice || currentPrice.Price >= upgradePrice.Price;
    dialogRef.content.instance.additionalCost = upgradeCostMontly;
    dialogRef.content.instance.upgradeFeatures = upgradeFeatures;
    dialogRef.content.instance.triggerMessage = triggerMessage;
  
    dialogRef.result.subscribe((result) => {
      if (result === 'confirm') {
        // Navigate to subscription update page
        this.router.navigate(['/settings/subscription', subscription.ID, 'update']);
      } else if (result === 'cancel' || result === undefined) {
        // Handle cancel or close action
        dialogRef.close();
        LogStatus('Upgrade modal closed or canceled.');
      }
    });
  }

  /**
   * Retrieves the aggregated Stripe subscription data by calling the corresponding GraphQL resolver.
   * @param subscription The subscription entity containing at least an ID.
   * @returns A promise that resolves to a StripeSubscriptionData object.
   */
  public async GetStripeSubscriptionData(subscription: SubscriptionEntityExtended): Promise<StripeSubscriptionData> {
    if (!subscription || !subscription.ID) {
      throw new Error('Invalid subscription.');
    }

    const query = gql`
      query GetStripeSubscriptionData($ID: String!) {
        GetStripeSubscriptionData(ID: $ID) {
          upcomingInvoice {
            amountDue
            currency
            status
            subtotal
            tax
            total
            billingDate
            periodStart
            periodEnd
            totalExcludingTax
            appliedBalance
            lines {
              description
              quantity
              unitPrice
              amount
            }
          }
          invoiceHistory {
            id
            amountDue
            currency
            status
            invoiceNumber
            customerEmail
            dueDate
            created
            subtotal
            tax
            total
            lines {
              description
              quantity
              unitPrice
              amount
            }
          }
          currentPeriod {
            start
            end
          }
          paymentMethod {
            id
            type
            card {
              brand
              last4
              exp_month
              exp_year
            }
          }
        }
      }
    `;

    const result = await GraphQLDataProvider.Instance.ExecuteGQL(query, { ID: subscription.ID });
    return result.GetStripeSubscriptionData as StripeSubscriptionData;
  }

  /**
   * Retrieves a preview of the subscription update by calling the corresponding GraphQL resolver.
   * @param subscription The subscription entity containing at least an ID.
   * @param options A SubscriptionPreviewOptions object containing optional parameters for the preview (e.g., new price ID and quantity).
   * @returns A promise that resolves to a SubscriptionUpdatePreview object or null if not found.
   */
  public async GetSubscriptionUpdatePreview(subscription: SubscriptionEntityExtended, options: SubscriptionPreviewOptions ): Promise<SubscriptionUpdatePreview | null> {
    if (!subscription?.ID) {
      throw new Error('Invalid subscription.');
    }

    const query = gql`
      query GetSubscriptionUpdatePreview($ID: String!, $newPriceID: String, $newUserCount: Float) {
        GetSubscriptionUpdatePreview(
          ID: $ID
          newPriceID: $newPriceID
          newUserCount: $newUserCount
        ) {
          oldPlanName
          newPlanName
          lineItems {
            description
            amount
            quantity
            unitPrice
          }
          subtotal
          tax
          total
          appliedBalance
          nextPaymentAttempt
          currency
        }
      }
    `;

    const variables = {
      ID: subscription.ID,
      newPriceID: options.NewPriceID || null,
      newUserCount: options.NewUserCount || null,
    };

    const result = await GraphQLDataProvider.Instance.ExecuteGQL(query, variables);
    return result.GetSubscriptionUpdatePreview;
  }

  /**
   * Retrieves the billing session URL for updating the subscription payment method.
   * @param subscription The subscription entity containing at least an ID.
   * @returns A promise that resolves to the billing session URL string.
   */
  public async GetStripeBillingSessionURL(subscription: SubscriptionEntityExtended): Promise<string> {
    if (!subscription || !subscription.ID) {
      throw new Error('Invalid subscription.');
    }

    const query = gql`
      query GetStripeSubscriptionBillingSessionURL($ID: String!) {
        GetStripeSubscriptionBillingSessionURL(ID: $ID)
      }
    `;

    const result = await GraphQLDataProvider.Instance.ExecuteGQL(query, { ID: subscription.ID });
    return result.GetStripeSubscriptionBillingSessionURL;
  }

  /**
   * Creates a SetupIntent for adding a new payment method to a subscription
   * @param subscription The subscription entity containing at least an ID
   * @returns A promise that resolves to the SetupIntent's client secret
   */
  public async CreateSetupIntent(subscription: SubscriptionEntityExtended): Promise<string> {
    if (!subscription || !subscription.ID) {
      throw new Error('Invalid subscription.');
    }

    const query = gql`
      mutation CreateSubscriptionSetupIntent($ID: String!) {
        CreateSubscriptionSetupIntent(ID: $ID) {
          clientSecret
        }
      }
    `;

    const result = await GraphQLDataProvider.Instance.ExecuteGQL(query, { ID: subscription.ID });
    return result.CreateSubscriptionSetupIntent.clientSecret;
  }

  /**
   * Updates the default payment method for a subscription
   * @param subscription The subscription entity containing at least an ID
   * @param paymentMethodId The ID of the payment method to set as default
   * @returns A promise that resolves to a success status and optional error message
   */
  public async UpdateSubscriptionPaymentMethod(subscription: SubscriptionEntityExtended, paymentMethodId: string): Promise<{ success: boolean; error?: string }> {
    if (!subscription || !subscription.ID) {
      throw new Error('Invalid subscription.');
    }

    const query = gql`
      mutation UpdateSubscriptionPaymentMethod($ID: String!, $paymentMethodId: String!) {
        UpdateSubscriptionPaymentMethod(ID: $ID, paymentMethodId: $paymentMethodId) {
          success
          error
        }
      }
    `;

    const result = await GraphQLDataProvider.Instance.ExecuteGQL(query, { 
      ID: subscription.ID,
      paymentMethodId
    });
    return result.UpdateSubscriptionPaymentMethod;
  }

  private constructSubscriptionPlan(plans: PlanEntityExtended[], details: PlanDetailEntityExtended[], prices: PlanPriceEntityExtended[], features: any[]): PlanDetails[] {
    const result: PlanDetails[] = [];

    for (const plan of plans) {
      const planDetails = details.filter((d) => d.PlanID === plan.ID);
      const planPrices = prices.filter((p) => p.PlanID === plan.ID);
      let planFeatures = [];
      
      if (planDetails.length === 1) {
        planFeatures = features.filter((f) => f.PlanDetailID === planDetails[0].ID).map((f) => f.Feature);
      }

      result.push({
        Plan: plan,
        Constraints: planDetails[0],
        Prices: planPrices,
        Features: planFeatures
      });
    }

    return result;
  }

  private handlePlanPriceChange(historyRecord: SubscriptionHistoryEntity, oldPriceID: string, newPriceID: string): void {
    const oldPlanPrice = this.plans.flatMap(p => p.Prices).find(pp => pp.ID === oldPriceID);
    const newPlanPrice = this.plans.flatMap(p => p.Prices).find(pp => pp.ID === newPriceID);
    const oldPlan = this.plans.flatMap(p => p.Plan).find(pl => pl.ID === oldPlanPrice.PlanID);
    const newPlan = this.plans.flatMap(p => p.Plan).find(pl => pl.ID === newPlanPrice.PlanID);

    historyRecord.OldPlanPriceID = oldPlanPrice.ID;
    historyRecord.NewPlanPriceID = newPlanPrice.ID;

    if (oldPlan.ID !== newPlan.ID) {
        historyRecord.ChangeType = oldPlan.Sequence < newPlan.Sequence ? 'Upgrade' : 'Downgrade';
        historyRecord.Comments = `Plan changed from ${oldPlan.Name} to ${newPlan.Name}.`;
    } else {
        historyRecord.ChangeType = 'Billing Cycle';
        historyRecord.Comments = `Billing cycle changed from ${oldPlanPrice.BillingCycle} to ${newPlanPrice.BillingCycle}.`;
    }
  }

  private handleUserCountChange(historyRecord: SubscriptionHistoryEntity, oldQuantity: number, newQuantity: number): void {
    historyRecord.OldUserCount = oldQuantity;
    historyRecord.NewUserCount = newQuantity;
    historyRecord.ChangeType = 'User Count';
    historyRecord.Comments = `User count changed from ${oldQuantity} to ${newQuantity}.`;
  }

  private handleInstanceCountChange(historyRecord: SubscriptionHistoryEntity, oldCount: number, newCount: number): void {
    historyRecord.OldInstanceCount = oldCount;
    historyRecord.NewInstanceCount = newCount;
    historyRecord.ChangeType = newCount > oldCount ? 'Instance Added' : 'Instance Removed';
    historyRecord.Comments = `Instance count changed from ${oldCount} to ${newCount}.`;
  }
}